/**
 * useDailyExerciseData Hook
 *
 * Manages data fetching and state for daily exercise (test) editing.
 * Handles:
 * - Fetching test data by ID
 * - Managing test metadata state
 * - Managing questions state
 * - Fetching programs list
 * - Save/update operations
 */

import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { Test, Asset, TestTypes, AssetType } from '../../../graphql/API';
import type { QuestionWithNewFields } from '../../Questions/QuestionTypes';
import { TestsService } from '../TestsService';
import { ProgramsNewService } from '../../Program/ProgramsNewService';
import { QUESTION_TYPE } from '../../../graphql/API';

const testsService = new TestsService();
const programsNewService = new ProgramsNewService();

/**
 * Default empty question template
 */
/**
 * Creates an empty question template for NEW questions.
 * Uses raw npm-pikado-logic payload structure.
 */
const createEmptyQuestion = (): QuestionWithNewFields => ({
  id: '',  // Will be set by backend when saved
  name: '',
  correctAsset: {
    type: AssetType.AUDIO,
    thumbnailUri: '',
    fileUri: '',
  } as Asset,
  asset: {
    type: AssetType.PHOTO,
    thumbnailUri: '',
    fileUri: '',
  } as Asset,
  summary: 'Record with your own voice',
  description:
    'Press the photo or the words to listen to the pronunciation and then press the record button to record it with your own voice.',
  text: '',
  textAI: '',
  type: QUESTION_TYPE.READ_SENTENCE,
  program: '',
  difficulty: 'NORMAL',
  choices: [],
  // Initialize empty payload structure (raw API format)
  payload: {
    blanks: [],  // Will be populated when user adds CHOICES keywords
    media: [],   // Will be populated from asset/correctAsset
  },
} as QuestionWithNewFields);

export interface UseDailyExerciseDataReturn {
  // Test metadata state
  testName: string;
  setTestName: (name: string) => void;
  testType: TestTypes | undefined;
  setTestType: (type: TestTypes | undefined) => void;
  testDescription: string;
  setTestDescription: (description: string) => void;
  testSummary: string;
  setTestSummary: (summary: string) => void;
  testAsset: Asset | undefined;
  setTestAsset: (asset: Asset | undefined) => void;
  thumbnail: string | undefined;
  media: Array<{ type: string; url: string }>;

  // Questions state
  questions: QuestionWithNewFields[];
  setQuestions: (questions: QuestionWithNewFields[]) => void;
  activeQuestionIndex: number;
  setActiveQuestionIndex: (index: number) => void;
  addNewQuestion: () => void;
  deleteQuestion: (index: number) => Promise<void>;

  // Programs data
  programsList: any[];

  // Original test data (for update operations)
  originalTest: Test | undefined;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;

  // Operations
  fetchTestData: (testId: string) => Promise<void>;
  saveTest: () => Promise<void>;
}

/**
 * Custom hook for managing daily exercise data and operations
 */
export const useDailyExerciseData = (testId?: string): UseDailyExerciseDataReturn => {
  // Test metadata state
  const [testName, setTestName] = useState<string>('');
  const [testType, setTestType] = useState<TestTypes | undefined>(TestTypes.QUIZ);
  const [testDescription, setTestDescription] = useState<string>(
    'Watch the video at least 2 times and after that record the questions\' answers.'
  );
  const [testSummary, setTestSummary] = useState<string>(
    'Watch carefully at least 2 times and answer the questions.'
  );
  const [testAsset, setTestAsset] = useState<Asset | undefined>({
    type: AssetType.VIDEO,
    thumbnailUri: '',
    fileUri: '',
  } as Asset);
  const [thumbnail, setThumbnail] = useState<string | undefined>();
  const [media, setMedia] = useState<Array<{ type: string; url: string }>>([]);

  // Questions state
  const [questions, setQuestions] = useState<QuestionWithNewFields[]>([createEmptyQuestion()]);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(0);

  // Programs data
  const [programsList, setProgramsList] = useState<any[]>([]);

  // Original test data
  const [originalTest, setOriginalTest] = useState<Test | undefined>();

  // Loading states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  /**
   * Ensures question has proper ID mapping for MongoDB compatibility.
   * Works with raw API data - no parsing or transformation.
   */
  const processQuestion = (question: QuestionWithNewFields): QuestionWithNewFields => {
    // Make a shallow copy to avoid mutating the original
    const processedQuestion = { ...question };

    // Ensure both id and _id are set for MongoDB compatibility
    if (!processedQuestion.id && !processedQuestion._id) {
      console.error('Question missing both id and _id:', processedQuestion);
    } else if (processedQuestion._id && !processedQuestion.id) {
      processedQuestion.id = processedQuestion._id;
    } else if (processedQuestion.id && !processedQuestion._id) {
      processedQuestion._id = processedQuestion.id;
    }

    return processedQuestion;
  };

  /**
   * Fetches test data by ID and initializes state
   */
  const fetchTestData = useCallback(async (testId: string) => {
    try {
      setIsLoading(true);

      const testData: any = await testsService.getTestById(testId);

      // Process all questions
      const processedQuestions = testData.questions.map((q: any) =>
        processQuestion(q as QuestionWithNewFields)
      );

      // Update state
      setOriginalTest(testData);
      setTestName(testData.name);
      setTestType(testData.type);
      setTestDescription(testData.description);
      setTestSummary(testData.summary);
      setTestAsset(testData.asset);
      setThumbnail(testData.thumbnail);
      setMedia(testData.media || []);
      setQuestions(processedQuestions);
      setActiveQuestionIndex(0);
    } catch (error) {
      console.error('Error fetching test:', error);
      message.error('Failed to load test data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetches programs list on mount
   */
  useEffect(() => {
    programsNewService
      .getNewPrograms()
      .then((programs) => {
        console.log('Programs loaded:', programs);
        setProgramsList(programs);
      })
      .catch((error) => {
        console.error('Error fetching programs:', error);
        message.error('Failed to load programs list');
      });
  }, []);

  /**
   * Fetches test data when testId is provided
   */
  useEffect(() => {
    if (testId) {
      fetchTestData(testId);
    }
  }, [testId, fetchTestData]);

  /**
   * Adds a new empty question
   */
  const addNewQuestion = useCallback(() => {
    const newQuestion = createEmptyQuestion();
    setQuestions((prev) => [...prev, newQuestion]);
    setActiveQuestionIndex(questions.length);
  }, [questions.length]);

  /**
   * Deletes a question by index
   */
  const deleteQuestion = useCallback(
    async (index: number) => {
      try {
        const questionToDelete = questions[index];
        const questionId = questionToDelete._id || questionToDelete.id;

        if (!questionId) {
          throw new Error('Question ID is missing. Cannot delete question without an ID.');
        }

        setIsSaving(true);

        // Import QuestionsService here to avoid circular dependency
        const { QuestionsService } = await import('../../Questions/QuestionsService');
        const questionsService = new QuestionsService();

        await questionsService.deleteQuestion(questionId);

        // Remove from local state
        const updatedQuestions = questions.filter((_, i) => i !== index);
        setQuestions(updatedQuestions);

        // Adjust active index if needed
        if (index <= activeQuestionIndex && activeQuestionIndex > 0) {
          setActiveQuestionIndex(activeQuestionIndex - 1);
        }

        message.success('Question deleted successfully');
      } catch (error) {
        console.error('Delete error:', error);
        message.error('Failed to delete question');
      } finally {
        setIsSaving(false);
      }
    },
    [questions, activeQuestionIndex]
  );

  /**
   * Saves the test (update operation)
   */
  const saveTest = useCallback(async () => {
    try {
      if (!originalTest) {
        throw new Error('No original test data to update');
      }

      setIsSaving(true);

      const testData: any = {
        id: originalTest.id,
        updatedAt: originalTest.updatedAt,
        questions: questions,
        type: testType,
        name: testName,
        description: testDescription,
        summary: testSummary,
        asset: testAsset,
      };

      console.log('Saving test data:', testData);

      // Note: Validation is handled in TestEdit.tsx using validateTestMetadata() and validateAllQuestions()
      // before saveTest() is called. No need to duplicate validation here.

      // Update test
      const response = await testsService.updateTest(testData, originalTest);

      message.success(`Test updated successfully! ID: ${response?.id}`);

      // Return response for navigation or other actions
      return response;
    } catch (error) {
      console.error('Error saving test:', error);
      message.error('Failed to save test');
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [
    originalTest,
    questions,
    testType,
    testName,
    testDescription,
    testSummary,
    testAsset,
  ]);

  return {
    // Test metadata
    testName,
    setTestName,
    testType,
    setTestType,
    testDescription,
    setTestDescription,
    testSummary,
    setTestSummary,
    testAsset,
    setTestAsset,
    thumbnail,
    media,

    // Questions
    questions,
    setQuestions,
    activeQuestionIndex,
    setActiveQuestionIndex,
    addNewQuestion,
    deleteQuestion,

    // Programs
    programsList,

    // Original data
    originalTest,

    // Loading states
    isLoading,
    isSaving,

    // Operations
    fetchTestData,
    saveTest,
  };
};
